import { Buffer } from 'buffer';

import React, { useCallback, useMemo } from 'react';

import FileDownload from '@mui/icons-material/FileDownload';
import { AlertTitle, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import {
    DropZoneContainer,
    ErrorFooter,
    ErrorRow,
    Errors,
    StyledAlert,
    StyledChooseFileContainer,
    StyledCloudIconContainer,
    StyledCloudUploadIcon,
    StyledDropZone,
    StyledErrorTypography,
    StyledLink,
    StyledMaxAlert,
    TemplateLink,
    TemplateLinkContainer
} from './styles';
import { UserFromCSV } from './types';
import { parseUsersCsv } from './utils/parseUsersCsv';
import AdminUsersContainer from '../../AdminUsersContainer';
import { validateParsedUsers } from '../../utils/userInputValidation';

enum FileErrorCode {
    'file-too-large',
    'too-many-files',
    'file-invalid-type'
}

const MAX_USER_LIMIT = 1000;

export const FileUploader: React.FC = () => {
    const { t } = useTranslation('portal');
    const {
        usersForBulkUpload,
        setUsersForBulkUpload,
        setFileNameForCSV,
        fileNameForCSV,
        setErrorIndexes,
        errorIndexes,
        duplicateIndexes,
        setDuplicateIndexes,
        errorsAcknowledged,
        setErrorsAcknowledged,
        maxUsersLimitExceeded,
        setMaxUsersLimitExceeded
    } = useContainer(AdminUsersContainer);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const onDropAccepted = useCallback(
        (acceptedFiles: File[]) => {
            closeSnackbar();
            const fileName = acceptedFiles?.[0]?.name;
            const isCsv = fileName?.endsWith('.csv') ?? false;

            if (!isCsv) {
                const message = t('admin-invite.file-invalid-type');
                enqueueSnackbar(message, { variant: 'error', persist: true });
            } else {
                const reader = new FileReader();

                reader.onload = async (event: ProgressEvent<FileReader>) => {
                    const fileContents = (event?.target?.result as ArrayBuffer) ?? [];
                    const contentsAsUint8Array = new Uint8Array(fileContents);
                    const fileText = Buffer.from(contentsAsUint8Array).toString('utf8');

                    const formattedData = parseUsersCsv(fileText);
                    const { limitExceeded, newErrorIndexes, indexesOfDuplicateEmails } = validateParsedUsers({
                        users: formattedData,
                        maxCsvRows: MAX_USER_LIMIT
                    });

                    setMaxUsersLimitExceeded(limitExceeded);
                    setErrorIndexes(newErrorIndexes);
                    setDuplicateIndexes(indexesOfDuplicateEmails);
                    setUsersForBulkUpload(formattedData);
                    setFileNameForCSV(fileName);
                };

                acceptedFiles.forEach((file) => {
                    reader.readAsArrayBuffer(file);
                });
            }
        },
        [
            closeSnackbar,
            enqueueSnackbar,
            setUsersForBulkUpload,
            setDuplicateIndexes,
            setErrorIndexes,
            setFileNameForCSV,
            setMaxUsersLimitExceeded,
            t
        ]
    );

    const onDropRejected = useCallback(
        (fileRejections) => {
            closeSnackbar();
            const errorCode: FileErrorCode = fileRejections[0].errors[0].code;

            if (errorCode in FileErrorCode) {
                const message = t(`admin-invite.${errorCode}`);
                enqueueSnackbar(message, { variant: 'error', persist: true });
            } else {
                const genericMessage = t('admin-invite.generic-upload-error');
                enqueueSnackbar(genericMessage, { variant: 'error', persist: true });
            }
        },
        [closeSnackbar, enqueueSnackbar, t]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted,
        onDropRejected,
        accept: '', // MIME types not properly set on windows. Validate fileType in onDropAccepted
        maxSize: 2097152, // 2mb
        maxFiles: 1
    });

    const handleToggle = () => {
        setErrorsAcknowledged((prevState) => !prevState);
    };

    interface ErrorUser extends UserFromCSV {
        row: number;
        duplicate?: boolean;
    }

    const errorUsers = useMemo(() => {
        if (errorIndexes.length || duplicateIndexes.length) {
            const errors: ErrorUser[] = [];

            usersForBulkUpload.forEach((admin, index) => {
                if (errorIndexes.includes(index)) {
                    errors.push({
                        ...admin,
                        row: index + 1
                    });
                }

                if (duplicateIndexes.includes(index)) {
                    errors.push({
                        ...admin,
                        row: index + 1,
                        duplicate: true
                    });
                }
            });

            return errors;
        }

        return [];
    }, [errorIndexes, duplicateIndexes, usersForBulkUpload]);

    const prepareDataForCsv = (users: UserFromCSV[]) => {
        let csv = 'name,email\n';

        users.forEach(({ name, email }) => {
            csv += `${name},${email}\n`;
        });

        return csv;
    };

    const downloadUserErrorsCsv = (): string => {
        const data = prepareDataForCsv(errorUsers);
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], { type: 'text/csv;charset=utf-8;' });
        return URL.createObjectURL(blob);
    };

    const downloadTemplateCsv = (): string => {
        const data = prepareDataForCsv([{ name: 'Jane Doe', email: 'jane.doe@example.com' }]);
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], { type: 'text/csv;charset=utf-8;' });
        return URL.createObjectURL(blob);
    };

    const renderErrorsContainer = () => {
        if (maxUsersLimitExceeded) {
            return (
                <StyledMaxAlert severity="error">
                    <AlertTitle sx={{ fontWeight: 'bold', margin: 0 }}>
                        {t('users.max-users-exceeded', {
                            rows: usersForBulkUpload.length,
                            maxRowsAllowed: MAX_USER_LIMIT
                        })}
                    </AlertTitle>
                </StyledMaxAlert>
            );
        }

        if (errorUsers.length) {
            return (
                <>
                    <StyledAlert severity="error">
                        <AlertTitle sx={{ fontWeight: 'bold' }}>
                            {t('users.email-errors-detected', { emailErrorCount: errorUsers.length })}
                        </AlertTitle>
                        <Errors>
                            {errorUsers.map((admin, i) => (
                                <ErrorRow key={i}>
                                    <StyledErrorTypography sx={{ fontWeight: 'bold' }}>
                                        {t('admin-invite.row-error', { rowNumber: admin.row })}
                                    </StyledErrorTypography>
                                    <StyledErrorTypography>{admin.name},</StyledErrorTypography>
                                    <StyledErrorTypography>
                                        {admin.email} {admin.duplicate ? t('admin-invite.duplicate-email') : null}
                                    </StyledErrorTypography>
                                </ErrorRow>
                            ))}
                        </Errors>
                    </StyledAlert>
                    <ErrorFooter>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={() => handleToggle()}
                                    checked={errorsAcknowledged}
                                    name="acknowledge-errors"
                                />
                            }
                            label={t('admin-invite.upload-without-these-rows')}
                        />
                        <StyledLink
                            download={`${t('admin-invite.errors-file-name')}.csv`}
                            target="_self"
                            underline="none"
                            href={downloadUserErrorsCsv()}
                        >
                            <FileDownload />
                            {t('admin-invite.download-exceptions', { exceptionsCount: errorUsers.length })}
                        </StyledLink>
                    </ErrorFooter>
                </>
            );
        }

        return null;
    };

    return (
        <DropZoneContainer>
            <Typography>{t('users.upload-instruction')}</Typography>
            <StyledDropZone {...getRootProps()} isDragActive={isDragActive}>
                <input {...getInputProps()} />
                <StyledCloudIconContainer>
                    <StyledCloudUploadIcon color="primary" />
                </StyledCloudIconContainer>
                <Typography align="center" color="textSecondary">
                    {t('users.upload-instruction-drag-and-drop')}
                </Typography>
                <StyledChooseFileContainer>
                    {fileNameForCSV ? (
                        <Button color="primary" variant="outlined">
                            {t('users.replace-file')}
                        </Button>
                    ) : (
                        <Button color="primary" variant="contained">
                            {t('users.choose-file')}
                        </Button>
                    )}
                </StyledChooseFileContainer>
                <TemplateLinkContainer>
                    <TemplateLink
                        onClick={(e) => e.stopPropagation()}
                        download={`${t('admin-invite.template-file-name')}.csv`}
                        target="_self"
                        underline="none"
                        href={downloadTemplateCsv()}
                    >
                        <FileDownload />
                        {t('admin-invite.download-template')}
                    </TemplateLink>
                </TemplateLinkContainer>
            </StyledDropZone>
            <Typography sx={{ marginBottom: '1rem' }} variant="body1">
                {fileNameForCSV}
            </Typography>
            {renderErrorsContainer()}
        </DropZoneContainer>
    );
};
